import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Flag } from "lucide-react";
import { verifyFamily, verifyFamilyAdd, verifyFamilyDel, verifyJobs, verifyEvidence, login } from "../../lib/api";
import { LIGHT } from "./VerdictCard";

const Login = ({ onOk }) => {
  const [pin, setPin] = useState("");
  const go = () => login(pin).then(onOk).catch(() => toast.error("Wrong PIN"));
  return (
    <div className="max-w-sm mx-auto mt-24 space-y-3" data-testid="verify-admin-login">
      <p className="text-cyan-50 font-bold text-xl">Verify Bar admin</p>
      <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} placeholder="Owner PIN"
        className="w-full rounded-xl bg-black/40 border border-cyan-500/30 p-3 text-cyan-50" data-testid="verify-admin-pin" />
      <button onClick={go} className="w-full rounded-full py-2 bg-[#00F0FF] text-[#030712] font-black" data-testid="verify-admin-login-btn">Sign in</button>
    </div>
  );
};

const FamilyPanel = ({ items, reload }) => {
  const [f, setF] = useState({ name: "", phone: "", email: "", lang: "English" });
  const add = () => verifyFamilyAdd(f).then(() => { setF({ name: "", phone: "", email: "", lang: "English" }); reload(); toast.success("Added"); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  const inp = (k, ph) => <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="rounded-lg bg-black/40 border border-cyan-500/30 px-2 py-1.5 text-sm text-cyan-50 min-w-0" data-testid={`verify-family-${k}`} />;
  return (
    <section className="rounded-2xl border border-cyan-500/30 bg-[#050b14] p-4 space-y-3" data-testid="verify-family-panel">
      <p className="font-bold text-cyan-50">Family circle</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {inp("name", "Name")}{inp("phone", "+1 phone")}{inp("email", "email")}
        <select value={f.lang} onChange={(e) => setF({ ...f, lang: e.target.value })} className="rounded-lg bg-black/40 border border-cyan-500/30 px-2 text-sm text-cyan-50" data-testid="verify-family-lang">
          {["English", "Gujarati", "Hindi"].map((l) => <option key={l}>{l}</option>)}
        </select>
        <button onClick={add} disabled={!f.name} className="rounded-full bg-[#00F0FF] text-[#030712] font-bold text-sm disabled:opacity-50" data-testid="verify-family-add">Add</button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-cyan-300/60 text-left"><th>Name</th><th>Phone</th><th>Email</th><th>Lang</th><th>Extension key</th><th /></tr></thead>
        <tbody>{items.map((m) => (
          <tr key={m.id} className="border-t border-white/5 text-cyan-50" data-testid={`verify-family-row-${m.id}`}>
            <td>{m.name}</td><td>{m.phone}</td><td>{m.email}</td><td>{m.lang}</td><td className="font-mono text-xs text-cyan-200/70">{m.key} <button className="ml-1 text-[#00F0FF] underline" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/verify?k=${m.key}`).then(() => toast.success("Personal link copied"))} data-testid={`verify-family-link-${m.id}`}>copy link</button></td>
            <td><button onClick={() => verifyFamilyDel(m.id).then(reload)} data-testid={`verify-family-del-${m.id}`}><Trash2 className="w-4 h-4 text-rose-300" /></button></td>
          </tr>))}
        </tbody>
      </table>
    </section>
  );
};

const JobLog = ({ data, reload }) => (
  <section className="rounded-2xl border border-cyan-500/30 bg-[#050b14] p-4 space-y-2" data-testid="verify-job-log">
    <p className="font-bold text-cyan-50">Job log</p>
    <p className="text-xs text-cyan-200/60">Engines: {Object.entries(data.engines || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
    <div className="space-y-2">{(data.items || []).map((j) => {
      const L = LIGHT[j.verdict?.light] || {};
      return (
        <details key={j.id} className="rounded-xl border border-white/10 p-2" data-testid={`verify-job-${j.id}`}>
          <summary className="cursor-pointer text-sm text-cyan-50 flex flex-wrap gap-2 items-center">
            <b style={{ color: L.c }}>{j.verdict?.light || j.status}</b>
            <span>{j.media_type}</span><span className="text-cyan-200/60">{j.channel} · {j.member || j.sender}</span>
            <span className="text-cyan-200/50">{new Date(j.created_at).toLocaleString()} CT</span>
            {j.evidence && <span className="text-amber-300 text-xs">EVIDENCE</span>}
            <span className="truncate max-w-[40ch] text-cyan-100/70">{j.verdict?.reason || j.note}</span>
          </summary>
          <div className="mt-2 text-xs text-cyan-100/80 space-y-1">
            {(j.engine_calls || []).map((c, i) => <p key={i}><b className={c.ok ? "text-emerald-300" : "text-rose-300"}>{c.engine}</b> {c.kind} · {c.ms}ms · {c.detail}</p>)}
            {(j.results || []).map((r, i) => <p key={`r${i}`} className="text-cyan-200/60">score {r.engine}: {r.risk ?? "n/a"}</p>)}
            <button onClick={() => verifyEvidence(j.id, !j.evidence).then(reload)} className="flex items-center gap-1 text-amber-300" data-testid={`verify-evidence-${j.id}`}><Flag className="w-3.5 h-3.5" /> {j.evidence ? "Unflag evidence" : "Keep as evidence (skip 24h delete)"}</button>
          </div>
        </details>
      );
    })}</div>
  </section>
);

export const VerifyAdmin = () => {
  const [fam, setFam] = useState(null);
  const [jobs, setJobs] = useState({ items: [] });
  const [authed, setAuthed] = useState(true);
  const reload = () => Promise.all([verifyFamily(), verifyJobs()]).then(([a, b]) => { setFam(a.items); setJobs(b); setAuthed(true); }).catch((e) => e?.response?.status === 401 && setAuthed(false));
  useEffect(() => { reload(); }, []);
  if (!authed) return <Login onOk={reload} />;
  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="verify-admin">
      <p className="text-2xl font-black text-cyan-50">Verify Bar · admin</p>
      <FamilyPanel items={fam || []} reload={reload} />
      <JobLog data={jobs} reload={reload} />
    </div>
  );
};
